import React, { useState } from 'react';
import * as Yup from 'yup';
import { CountryCode } from 'countries-and-timezones';
import { NumberParam, useQueryParams } from 'use-query-params';

import { artistProfileApi } from 'api';
import { en } from 'config/constants/localization';
import { COLORS } from 'config/constants';
import { FieldErrors, formatFieldErrors } from 'helpers/form';
import { canUserEditArtistMember } from 'helpers/policies';
import { useAppDispatch, useAppSelector } from 'hooks/redux';
import { showAlert } from 'redux/actions/ui';
import { selectUser } from 'redux/selectors';

import Button, { ButtonGroup } from 'components/Button';
import CommaList from 'components/CommaList';
import FormGroup from 'components/Form/FormGroup';
import { FormikWrapper } from 'components/Form/Wrappers';
import Tooltip from 'components/Tooltip';
import WizardStep from 'components/Wizard/WizardStep';

import { ArtistMembersForm } from './ArtistMembersForm';
import { ArtistMemberLookup } from './ArtistMemberLookup';
import { ArtistMembersSubtitle } from './ArtistMembersSubtitle';
import { MemberDataField, getMissingArtistMemberFields } from './helpers';
import { ArtistMember, ArtistProfile, V2ProUser } from 'types';
import { NewMember, WizardContext, WizardProps } from 'components/Wizard/types';
import { FormikHelpers } from 'formik';
import { MemberDataSubtitle } from 'components/MemberDataSubtitle';

const getNewMemberState = () => ({ key: Date.now(), __newMember: true });

export const createArtistMemberValidation = (context: WizardContext) => {
  const isLabelContext = context === 'label-release';

  return Yup.object().shape({
    pro_email: Yup.string()
      .email('Please enter a valid email.')
      .required('An email is required.'),
    first_name: isLabelContext
      ? Yup.string().required('First name is required.')
      : Yup.string(),
    last_name: isLabelContext
      ? Yup.string().required('Last name is required.')
      : Yup.string(),
    address: Yup.string().optional(),
    country: isLabelContext
      ? Yup.mixed<CountryCode>().required('Country is required.')
      : Yup.mixed<CountryCode>()
  });
};

export interface FormValues {
  pro_email: string;
  first_name: string;
  last_name: string;
  country: CountryCode | '';
  address: string;
}

export interface MemberMissingFieldInfo {
  canEdit: boolean;
  missingFields: MemberDataField[];
}

interface ArtistMembersProps {
  artistProfile: ArtistProfile;
  canUserAddMember: boolean;
  viewOnly: boolean;
}

const isFieldErrorsError = (e: unknown): e is { error: FieldErrors } => {
  return !!(
    e &&
    typeof e === 'object' &&
    'error' in e &&
    e.error &&
    typeof e.error === 'object'
  );
};

export const ArtistMembers: React.FC<ArtistMembersProps & WizardProps> = ({
  artistProfile,
  canUserAddMember,
  viewOnly,
  ...rest
}) => {
  const { handlePrevious, handleContinue, ...wizardProps } = rest;
  const { context } = wizardProps;

  // memberId is defined when coming to the Wizard from a SoundSystem link for editing an artist member
  const [{ memberId: memberIdToEdit }] = useQueryParams({ memberId: NumberParam });

  const dispatch = useAppDispatch();
  const user = useAppSelector(selectUser);

  const [upsertArtistMember, { isLoading: isUpdating }] =
    artistProfileApi.useUpsertArtistMemberMutation();

  const noMembers = artistProfile?.members.length === 0;

  // Helpers for managing member state
  const [newMembers, setNewMembers] = useState<NewMember[]>(
    noMembers && canUserAddMember ? [getNewMemberState()] : []
  );
  const [existingMemberSelected, setExistingMemberSelected] = useState(false);
  const removeNewMember = (member: NewMember) =>
    setNewMembers(newMembers => newMembers.filter(({ key }) => key !== member.key));
  const addNewMember = () =>
    setNewMembers(newMembers => [...newMembers, getNewMemberState()]);
  const setNewMemberData = (memberKey: number, memberData: Partial<ArtistMember>) => {
    setNewMembers(newMembers =>
      newMembers.map(member =>
        member.key === memberKey
          ? {
              ...memberData,
              key: memberKey,
              __newMember: true
            }
          : member
      )
    );
  };

  const artistMembers = [...artistProfile.members, ...newMembers];

  // key relevant computed values by id/key
  const missingFieldsByMemberId: Record<number, MemberMissingFieldInfo> =
    artistMembers.reduce((accum, member) => {
      const canEdit = canUserEditArtistMember({
        artistProfile,
        currentUser: user,
        member
      });

      const missingFields = getMissingArtistMemberFields(member, context, canEdit);

      return {
        ...accum,
        [member.id || (member as NewMember).key]: {
          canEdit,
          missingFields
        }
      };
    }, {});

  const getMemberMissingFieldInfo = (member: ArtistMember | NewMember) =>
    missingFieldsByMemberId[member.id || (member as NewMember).key];

  const memberMissingRequiredFields = artistProfile?.members.some(member => {
    const { missingFields } = getMemberMissingFieldInfo(member);
    return missingFields.length > 0;
  });

  const subtitleFromMissingFields = (missingFields: MemberDataField[]) => (
    <span
      data-testid={ARTIST_MEMBERS_TEST_IDS.MISSING_MEMBER_INFO}
      style={{ color: 'red', fontWeight: 500 }}
    >
      Missing: <CommaList data={missingFields} attribute="label" />
    </span>
  );

  return (
    <WizardStep
      {...wizardProps}
      title={(() => {
        if (artistProfile?.members.length === 0) return 'Setup Artist Members';
        if (canUserAddMember) return 'Add Artist Members';
        return 'Artist Members';
      })()}
      subtitle={
        <ArtistMembersSubtitle
          artistProfile={artistProfile}
          artistMembers={artistMembers}
          viewOnly={viewOnly}
          getMemberMissingFieldInfo={getMemberMissingFieldInfo}
        />
      }
      onContinue={() => {
        // NOTE: warnUnsavedChanges' in FormGroupRow.Formik will warn user if leaving page with unsaved changes
        // due to a "dirty" form, so we just need to make sure no new members started without dirtying forms
        const unsavedChanges = newMembers.length > 0;

        if (unsavedChanges) {
          const canProceed = window.confirm(en.warnings.unsavedChangesContinue);
          if (!canProceed) return;
        }

        // NOTE: all save functionality is handled by buttons within the ArtistMembersForm (see 'onSubmit' below)
        return handleContinue(() => Promise.resolve());
      }}
      onPrevious={handlePrevious}
      isContinueDisabled={noMembers || memberMissingRequiredFields || isUpdating}
      continueDisabledText={(() => {
        if (noMembers) {
          return 'Please add an artist member before continuing.';
        }

        if (memberMissingRequiredFields) {
          return 'Please complete the missing fields before continuing.';
        }
      })()}
    >
      <FormGroup className="pt-2 pb-1 mt-0 mb-0">
        <FormGroup.List>
          {artistMembers.map(member => {
            const { first_name, last_name, pro_email, country } = member;
            const isNewMember = !!(member as NewMember).__newMember;
            const { missingFields, canEdit: canEditMember } =
              getMemberMissingFieldInfo(member);
            const subtitle =
              missingFields.length > 0 ? (
                subtitleFromMissingFields(missingFields)
              ) : (
                <MemberDataSubtitle pro_email={pro_email} country={country} />
              );
            const noRecord = isNewMember && !member.id;

            return (
              <div
                data-testid={ARTIST_MEMBERS_TEST_IDS.MEMBER_ROW}
                key={isNewMember ? (member as NewMember).key : member.id}
              >
                <FormGroup.FormikRow
                  data-testid={
                    isNewMember
                      ? ARTIST_MEMBERS_TEST_IDS.NEW_MEMBER_ROW
                      : ARTIST_MEMBERS_TEST_IDS.getTestIdForExistingMember(member.id!)
                  }
                  title={
                    noRecord ? 'Member Info' : `${first_name || ''} ${last_name || ''}`
                  }
                  subtitle={
                    noRecord ? <div>Add contact details for the artist.</div> : subtitle
                  }
                  showSubtitleMobile
                  hideButton
                  editable={canEditMember || isNewMember}
                  disabled={!canEditMember}
                  initialEditState={
                    isNewMember || (memberIdToEdit === member.id && canEditMember)
                  }
                  // Hide the "done" button if new member
                  renderAside={isNewMember ? () => null : undefined}
                  // formik props:
                  initialValues={{
                    country: member.country || '',
                    address: member.address || '',
                    pro_email: member.pro_email || '',
                    first_name: member.first_name || '',
                    last_name: member.last_name || ''
                  }}
                  validationSchema={createArtistMemberValidation(context)}
                  warnUnsavedChanges
                  onSubmit={async (
                    values: FormValues,
                    actions: FormikHelpers<FormValues>
                  ) => {
                    // NOTE: We only want to pass member data for updated fields
                    const updatedOnly = (fieldName: keyof FormValues) =>
                      values[fieldName] !== member[fieldName]
                        ? values[fieldName]
                        : undefined;

                    try {
                      await upsertArtistMember({
                        artistId: artistProfile.id,
                        member: {
                          first_name: updatedOnly('first_name'),
                          last_name: updatedOnly('last_name'),
                          address: updatedOnly('address'),
                          country: updatedOnly('country'),
                          pro_email: updatedOnly('pro_email'),
                          pro_user_id: member.id
                        }
                      }).unwrap();
                      // upon successful addition of the artist member, we can remove them from local state because
                      // the artistProfile cache will be updated
                      removeNewMember(member as NewMember);
                      setExistingMemberSelected(false);
                    } catch (e: unknown) {
                      if (isFieldErrorsError(e)) {
                        // NOTE: Because of how the user models are structured in rails, we need to manually
                        // map back to the named fields in the client
                        const railsError = e.error;
                        const formattedErrors = formatFieldErrors(railsError);
                        actions.setErrors({
                          ...railsError,
                          first_name: formattedErrors.firstname,
                          last_name: formattedErrors.lastname,
                          pro_email: formattedErrors.email
                        });
                      }

                      dispatch(showAlert(e));
                      throw e;
                    }
                  }}
                >
                  {({ values, setValues, isSubmitting, errors }) => (
                    <>
                      <ArtistMembersForm
                        disabled={
                          isSubmitting ||
                          (existingMemberSelected &&
                            (missingFields.length === 0 || !canEditMember))
                        }
                        context={context}
                      />

                      {'__newMember' in member && member.__newMember && (
                        <ArtistMemberLookup
                          values={values as FormValues}
                          emailValid={!('pro_email' in errors)}
                          selectedMemberId={member.id}
                          setSelectedUser={(userData: FormValues | V2ProUser) => {
                            setNewMemberData(member.key, userData as ArtistMember);
                            setExistingMemberSelected(!!(userData as V2ProUser).id);
                            setValues({
                              first_name: userData.first_name ?? '',
                              last_name: userData.last_name ?? '',
                              pro_email: userData.pro_email ?? '',
                              country: userData.country ?? '',
                              address: userData.address ?? ''
                            });
                          }}
                        />
                      )}

                      <ButtonGroup>
                        <Tooltip
                          enabled={!canEditMember}
                          content="You aren't allowed to edit this user"
                        >
                          <FormikWrapper.SubmitButton
                            buttonText={isNewMember ? 'Add Member' : 'Save Member'}
                            isSubmitting={isSubmitting}
                            disabled={isNewMember ? false : !canEditMember}
                            data-testid={ARTIST_MEMBERS_TEST_IDS.SAVE_MEMBER_BUTTON}
                          />
                        </Tooltip>

                        {isNewMember && artistMembers.length > 1 && (
                          <Button
                            color={Button.COLORS.LINK}
                            style={{ color: COLORS.DANGER }}
                            className="ml-3"
                            onClick={() => removeNewMember(member as NewMember)}
                            type="button"
                            disabled={false}
                            data-testid={ARTIST_MEMBERS_TEST_IDS.REMOVE_MEMBER_BUTTON}
                          >
                            Remove Member
                          </Button>
                        )}
                      </ButtonGroup>
                    </>
                  )}
                </FormGroup.FormikRow>
              </div>
            );
          })}

          {canUserAddMember && (
            <FormGroup.Row editable={false}>
              <Button
                color={Button.COLORS.LINK}
                onClick={addNewMember}
                data-testid={ARTIST_MEMBERS_TEST_IDS.ADD_NEW_MEMBER_BUTTON}
              >
                Add Another Member
              </Button>
            </FormGroup.Row>
          )}
        </FormGroup.List>
      </FormGroup>
    </WizardStep>
  );
};

export const ARTIST_MEMBERS_TEST_IDS = {
  MEMBER_ROW: 'ArtistMembers-member-row--user',
  NEW_MEMBER_ROW: 'ArtistMembers-member-row--new',
  SAVE_MEMBER_BUTTON: 'ArtistMembers-member-row--save-member-btn',
  REMOVE_MEMBER_BUTTON: 'ArtistMembers-member-row--remove-member-btn',
  ADD_NEW_MEMBER_BUTTON: 'ArtistMembers-member-row--add-new-member-btn',
  MISSING_MEMBER_INFO: 'ArtistMembers-member-row--missing_info',
  getTestIdForExistingMember: (userId: number) =>
    `ArtistMembers-member-row--userId-${userId}`
};
